import React from 'react'
import {
  InteriorQuality as InteriorQualityType,
  RealEstateCategory,
  RealEstateFeatures,
  Salutation,
  SegmentationType,
  TransactionType,
} from '../../../../../@types/types'
import { Flow, FlowName, FlowScreenElement } from '../../../../../@types/flow-types'
import {
  SCREEN_ADDRESS,
  SCREEN_API_SEARCH_DATA,
  SCREEN_BASIC_INFO,
  SCREEN_CONSTRUCTION,
  SCREEN_CONTACT_INFO,
  SCREEN_COSTS,
  SCREEN_DESCRIPTION,
  SCREEN_MULTIMEDIA,
  SCREEN_OBJECT_NUMBERS,
  SCREEN_OTHER_FACILITIES,
  SCREEN_TENANT_DOCUMENTS,
  SCREENS,
} from '../../../../../services/domain/wizardScreens'
import Address from '../../../../../components/pages/insertionPage/screens/address/Address'
import { AGENT_APARTMENT_RENT_FLOW_PATH } from '../../../../../services/routes/routes'
import Description from '../../../../../components/pages/insertionPage/screens/description/Description'
import Multimedia from '../../../../../components/pages/insertionPage/screens/multimediaNew/Multimedia'

import ResidentialCostsRent from '../../../../../components/pages/insertionPage/screens/costs/wrappers/residential/ResidentialCostsRent'
import Construction from '../../../../../components/pages/insertionPage/screens/construction/Construction'
import TenantDocumentsScreen from '../../../../../components/pages/insertionPage/screens/tenantDocuments/TenantDocumentsScreen'
import { agentApartmentRentSchema } from './agentApartmentRentSchema'
import { MultimediaUploaderType } from '../../../../../@types/multimedia-types'
import {
  bathroomField,
  estateFeatures,
  floorAndTotalFloor,
  interiorQuality,
  noBedrooms,
  parkingSpace,
  petsAllowed,
  usableAreaField,
} from '../../../../../components/pages/insertionPage/screens/otherFacilities/wrapper/otherFacilitiesFieldsWrapper'
import OtherFacilities from '../../../../../components/pages/insertionPage/screens/otherFacilities/OtherFacilities'
import { BuildingTypeOption } from '../../../../../@types/dropdown-types'
import BasicInfo from '../../../../../components/pages/insertionPage/screens/basicInfo/BasicInfo'
import {
  livingSpaceField,
  priceField,
  priceMaximizer,
  roomField,
} from '../../../../../components/pages/insertionPage/screens/basicInfo/wrappers/basicInfoFieldsWrapper'
import { PriceFieldType } from '../../../../../components/pages/insertionPage/screens/basicInfo/fields/priceField/PriceField'
import { getDefaultClasses } from '../../../../../utils/style-utils'
import { buildingTypeField } from '../../../../../components/pages/insertionPage/screens/commonFields/buildingType/BuildingType'
import ContactInfo from '../../../../../components/pages/insertionPage/screens/contactInfo/ContactInfo'
import { courtageInformationToApi } from '../../../../../api/realEstate/converters/toApi/estate/courtageInformation/courtageInformationToApi'
import ApiSearchData from '../../../../../components/pages/insertionPage/screens/apiSearchData/ApiSearchData'
import { apiSearchDataToApi } from '../../../../../api/realEstate/converters/toApi/estate/apiSearchData/apiSearchDataToApi'
import { ObjectNumbersScreen } from '../../../../../components/pages/insertionPage/screens/objectNumber/ObjectNumbersScreen'
import { priceToApiMonthlyRent } from '../../../../../api/realEstate/converters/toApi/estate/price/priceToApi'

const agentApartmentRentFlowScreens: FlowScreenElement[] = [
  {
    screen: SCREENS[SCREEN_ADDRESS],
    element: (props) => <Address {...props} isPriceOverviewLinkDisplayed={false} />,
  },
  {
    screen: SCREENS[SCREEN_OBJECT_NUMBERS],
    element: (props) => <ObjectNumbersScreen {...props} />,
  },
  {
    screen: SCREENS[SCREEN_BASIC_INFO],
    element: (props) => (
      <BasicInfo
        {...props}
        screenTooltipKey='tooltip.screens.basicInfo.apartmentHouseOrSTARent'
        properties={[
          livingSpaceField(),
          roomField(),
          priceField(PriceFieldType.COLD_RENT, 'one-half palm-padding-left-m lap-padding-right-m desk-padding-right-m'),
          priceMaximizer(),
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_COSTS],
    element: (props) => (
      <ResidentialCostsRent
        {...props}
        showMoveInDate={true}
        showCourtage={true}
        screenTooltipKey='tooltip.screens.costs.allRent'
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_OTHER_FACILITIES],
    element: (props) => (
      <OtherFacilities
        {...props}
        screenTooltipKey='tooltip.screens.otherFacilities.allRent'
        properties={[
          estateFeatures([
            RealEstateFeatures.EQUIPPED_KITCHEN,
            RealEstateFeatures.BALCONY_TERRACE,
            RealEstateFeatures.GUEST_TOILET,
            RealEstateFeatures.SHARED_GARDEN,
            RealEstateFeatures.BASEMENT,
            RealEstateFeatures.FLAT_SHARE_SUITABLE,
            RealEstateFeatures.STEPLESS_ACCESSIBLE,
            RealEstateFeatures.CERTIFICATE_OF_ELIGIBILITY_NEEDED,
            RealEstateFeatures.ELEVATOR,
          ]),
          interiorQuality(),
          petsAllowed(),
          buildingTypeField(
            'apartment',
            [
              BuildingTypeOption.APARTMENT,
              BuildingTypeOption.ATTIC,
              BuildingTypeOption.BASEMENT,
              BuildingTypeOption.DUPLEX,
              BuildingTypeOption.ENTRESOL,
              BuildingTypeOption.GROUND_FLOOR,
              BuildingTypeOption.LOFT,
              BuildingTypeOption.PENTHOUSE,
              BuildingTypeOption.TERRACE,
              BuildingTypeOption.OTHER,
            ],
            getDefaultClasses('full')
          ),
          bathroomField(getDefaultClasses('left')),
          noBedrooms('right'),
          floorAndTotalFloor('left'),
          usableAreaField(undefined, getDefaultClasses('rightOnDesk')),
          parkingSpace({ showParkingSpaceType: true, hasGarageLabel: true }),
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_DESCRIPTION],
    element: (props) => <Description {...props} hasFurnishingNote={true} hasLocationNote={true} hasOtherNote={true} />,
  },
  {
    screen: SCREENS[SCREEN_MULTIMEDIA],
    element: (props) => (
      <Multimedia
        {...props}
        doCloseMultimediaStatusMessage={props.doCloseMultimediaStatusMessage!}
        allowMultimediaLinks={true}
        allowedTypesToDisplay={[
          MultimediaUploaderType.PICTURE,
          MultimediaUploaderType.FLOOR_PLAN,
          MultimediaUploaderType.DOCUMENT,
          MultimediaUploaderType.VIDEO,
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_CONSTRUCTION],
    element: (props) => <Construction {...props} hasLastModernizationField={true} />,
  },
  {
    screen: SCREENS[SCREEN_TENANT_DOCUMENTS],
    element: (props) => <TenantDocumentsScreen {...props} />,
  },
  {
    screen: SCREENS[SCREEN_API_SEARCH_DATA],
    element: (props) => <ApiSearchData {...props} />,
  },
  {
    screen: SCREENS[SCREEN_CONTACT_INFO],
    element: (props) => <ContactInfo {...props} hasContactManager={true} />,
  },
]

export const agentApartmentRentFlow: Flow = {
  flowInformation: {
    owner: 'wizard.navigation.header.agent',
    estateType: 'wizard.navigation.header.apartment',
    offerType: 'wizard.navigation.header.rents',
    path: AGENT_APARTMENT_RENT_FLOW_PATH,
    name: FlowName.AGENT_APARTMENT_RENT,
    validationSchema: agentApartmentRentSchema,
    options: {
      hasTextSuggestions: true,
    },
    metaData: {
      category: RealEstateCategory.APARTMENT,
      segmentation: SegmentationType.AGENT,
      transaction: TransactionType.RENT,
    },
    priceToApiMapper: priceToApiMonthlyRent,
    courtageToApiMapper: courtageInformationToApi,
    apiSearchDataToApiMapper: apiSearchDataToApi,
  },
  defaultStateValues: {
    heatingCostsOption: true,
    unknownConstructionYear: false,
    hideStreetAndStreetNumber: false,
    estateFeatures: {},
    interiorQuality: InteriorQualityType.NO_INFORMATION,
    salutation: Salutation.NO_SALUTATION,
  },
  flowScreens: agentApartmentRentFlowScreens,
}
